import { Route } from 'vue-router'
import store from '@/store'
import chunk from 'lodash/chunk'
import isEmpty from 'lodash/isEmpty'

import { User } from '../store/user/types'
import { Page, UserSearchFormParams } from '../type'
import SpecialtyType from './masters/SpecialtyType'
import FeatureType from './masters/FeatureType'

const MAX_INDICES_USERS = 500

export const COMPANY_SITE_URL = 'http://no-package.co.jp/'
export const PROFILE_TEMPLATE = `◆【前職】

◆【資格や役職】

◆【この業界に入った理由】

◆【仕事で大事にしてる事】

◆【趣味、特技】

◆【自己紹介】

◆【会社名】
`

export const needAutoSigninRoute = (route: Route) =>
  route.name === Page.Signin ||
  route.name === Page.Mypage ||
  route.name === Page.AdminUsers

export const isAdminRoute = (route: Route) => route.name === Page.AdminUsers

export const isUserSignedIn = () => store.getters['user/signedIn']

export const isUserAdmin = () => store.getters['user/isAdmin']

export const hasDataOperationAuthority = (userId: string) => {
  const user: User = store.getters['user/user']
  return user && (user.admin || user.id === userId)
}

export const chunkIndicesUsers = (users: User[]) =>
  chunk(users, MAX_INDICES_USERS)

export const hiraganaToKana = (str: string) =>
  str.replace(/[\u3041-\u3096]/g, match =>
    String.fromCharCode(match.charCodeAt(0) + 0x60)
  )

export const range = (start: number, end: number, step = 1) =>
  [...Array(Math.floor((end - start + step) / step))].map(
    (_, i) => start + i * step
  )

export const wrapPromise = async <T>(
  promise: Promise<T>
): Promise<T | Error> => {
  return promise.then(result => result).catch(error => error)
}

export const SortLogic = {
  default: (a: User, b: User) => {
    if (a.recommended && !b.recommended) return -1
    if (b.recommended && !a.recommended) return 1
    if (a.createdAt < b.createdAt) return -1
    if (b.createdAt < a.createdAt) return 1
    return 0
  }
}

export const FilterLogic = {
  byWord: (word: string, user: User) => {
    return (
      !word ||
      user.id.includes(word) ||
      user.email.includes(word) ||
      (user.familyName && user.familyName.includes(word)) ||
      (user.firstName && user.firstName.includes(word)) ||
      (user.profile &&
        hiraganaToKana(user.profile).includes(hiraganaToKana(word)))
    )
  },
  byFeatures: (featureTypes: FeatureType[], user: User) => {
    return (
      isEmpty(featureTypes) ||
      featureTypes.some(
        type => user.featureTypes && user.featureTypes.includes(type)
      )
    )
  },
  bySpecialties: (specialtyTypes: SpecialtyType[], user: User) => {
    return (
      isEmpty(specialtyTypes) ||
      (user.specialtyType && specialtyTypes.includes(user.specialtyType))
    )
  }
}

export const sortFilterUsers = (users: User[], params: UserSearchFormParams) =>
  users
    .filter(user => FilterLogic.byWord(params.word, user))
    .filter(user => FilterLogic.byFeatures(params.featureTypes, user))
    .filter(user => FilterLogic.bySpecialties(params.specialtyTypes, user))
    .sort((a: User, b: User) => {
      const featureMatchNumA = params.featureTypes.filter(
        type => a.featureTypes && a.featureTypes.includes(type)
      ).length
      const featureMatchNumB = params.featureTypes.filter(
        type => b.featureTypes && b.featureTypes.includes(type)
      ).length
      if (featureMatchNumA > featureMatchNumB) return -1
      if (featureMatchNumB > featureMatchNumA) return 1
      return SortLogic.default(a, b)
    })

export const isDevMode = process.env.NODE_ENV === 'development'
